import React, { useState, useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ThumbsUp, AlertCircle, ChevronRight } from "lucide-react";
import { useTRPC } from "@/trpc";
import {
  showNotInterestedToast,
  showNotInterestedErrorToast,
} from "../utils/show-not-interested-toast";
import { JobPostProvider } from "../matching/job-post-provider";
import { MatchStrengthBadge } from "../components/match-strength-badge";
import { cn } from "@/lib/utils";
import { queryClient } from "@/ajax/queryClient";
import { JobAcceptDialog, type AcceptableJob } from "../job-accept-dialog";
import { JobPostRemoveInterestDialog } from "../job-post-remove-interest-dialog";
import { HiringProcessChecklistDialog } from "@/features/hiring-process/hiring-process-checklist-dialog";
import { Link } from "@tanstack/react-router";
import { buttonVariants } from "@/components/ui/button";

export function JobInterests() {
  const trpc = useTRPC();

  const { mutateAsync: setDecision } = useMutation(
    trpc.jobPost.setDecision.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          trpc.jobPost.interested.queryOptions(),
        );
      },
    }),
  );

  const [acceptedJob, setAcceptedJob] = useState<AcceptableJob | null>(null);
  const [removeInterestJobId, setRemoveInterestJobId] = useState<string | null>(
    null,
  );
  const [removeInterestPreviousDecision, setRemoveInterestPreviousDecision] =
    useState<boolean | null>(null);
  const [checklistJobId, setChecklistJobId] = useState<string | null>(null);

  // --- Fetch Job Data ---
  // Ensure the query fetches necessary fields for filtering
  const {
    data: jobs,
    isLoading,
    error: _error,
  } = useQuery(trpc.jobPost.interested.queryOptions());

  const { data: hiringInstancesData } = useQuery(
    trpc.hiringProcess.getMyProcesses.queryOptions(),
  );

  type HiringInstance = NonNullable<typeof hiringInstancesData>[number];

  const hiringInstanceByJobPostId = useMemo(() => {
    if (!hiringInstancesData) {
      return new Map<string, HiringInstance>();
    }
    return new Map<string, HiringInstance>(
      hiringInstancesData.map((instance) => [instance.jobPostId, instance]),
    );
  }, [hiringInstancesData]);

  // --- Event Handlers ---
  const handleInterestChange = async ({
    jobId,
    isInterested,
  }: {
    jobId: string;
    isInterested: boolean | null;
  }) => {
    if (isInterested === false) {
      try {
        await setDecision({ id: jobId, interested: false });
        showNotInterestedToast();
      } catch {
        showNotInterestedErrorToast();
      }
      return;
    }
    const job = jobs?.find((j) => j.id === jobId);
    if (isInterested === true) {
      setAcceptedJob({
        id: jobId,
        externalUrl: job?.externalUrl ?? null,
        eligibility: job?.eligibility,
      });
      return;
    }
    if (isInterested === null) {
      setRemoveInterestJobId(jobId);
      setRemoveInterestPreviousDecision(job?.decision?.interested ?? null);
      return;
    }
  };

  const handleInterestSuccess = async () => {
    await queryClient.invalidateQueries(trpc.jobPost.interested.queryOptions());
  };

  if (jobs && jobs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8">
        <ThumbsUp className="mx-auto mb-4 h-16 w-16 rounded-2xl bg-primary/5 p-3 text-primary" />
        <p className="text-lg sm:text-xl font-medium text-slate-700 mb-2">
          You have not shown interest in any jobs yet.
        </p>
        <p className="text-sm sm:text-base text-slate-500 max-w-md mx-auto">
          Thumbs up on a job to show interest.
        </p>
        <Link
          to="/provider/jobs/search"
          className={buttonVariants({ className: "mt-6" })}
        >
          Find Available Jobs
        </Link>
      </div>
    );
  }

  return (
    <div className="h-full">
      {/* Same column as the cards, matching the recommended jobs page. */}
      <div className="mx-auto max-w-xl px-4 py-8 sm:px-0 sm:py-10">
        <header className="mb-4 px-1">
          <h1 className="text-2xl font-bold text-slate-900">Job Interests</h1>
        </header>
        {/* --- Job List --- */}
        <div className="space-y-5">
          {isLoading && (
            <div className="flex justify-center items-center h-screen">
              <p className="text-lg text-slate-600">Loading job interests...</p>
            </div>
          )}
          {jobs &&
            jobs.length > 0 &&
            jobs.map((job) => {
              const hiringInstance = hiringInstanceByJobPostId.get(job.id);
              const providerItems = hiringInstance
                ? hiringInstance.items.filter(
                    (item) => item.assigneeRole === "provider",
                  )
                : ([] as HiringInstance["items"]);
              const doneItems = providerItems.filter((item) =>
                (
                  ["completed", "skipped", "not_applicable"] as string[]
                ).includes(item.status),
              );
              const pendingCount = providerItems.length - doneItems.length;
              const hasPendingItems =
                !!hiringInstance &&
                providerItems.length > 0 &&
                pendingCount > 0;

              return (
                <div
                  key={job.id}
                  className={cn(
                    hasPendingItems &&
                      "rounded-2xl overflow-hidden shadow-sm border border-amber-300 ring-1 ring-amber-100",
                  )}
                >
                  {hasPendingItems && (
                    <button
                      type="button"
                      onClick={() => setChecklistJobId(job.id)}
                      className="flex min-h-12 w-full cursor-pointer items-center gap-3 bg-amber-50 px-4 py-3 text-left text-sm font-medium text-amber-800 transition-colors hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                    >
                      <div className="p-1.5 bg-amber-100 rounded-lg">
                        <AlertCircle size={16} className="text-amber-600" />
                      </div>
                      <span className="flex-1">
                        {pendingCount} required{" "}
                        {pendingCount === 1 ? "item" : "items"} to complete
                      </span>
                      <ChevronRight size={16} className="text-amber-400" />
                    </button>
                  )}
                  <JobPostProvider
                    jobData={job}
                    onInterestChange={handleInterestChange}
                    enableSwipe={false}
                    titleAs="h2"
                    headerAccessory={<MatchStrengthBadge match={job.match} />}
                    className={cn(
                      hasPendingItems && "rounded-none border-0 shadow-none",
                    )}
                  />
                </div>
              );
            })}
        </div>
      </div>
      {acceptedJob && (
        <JobAcceptDialog
          job={acceptedJob}
          onClose={() => setAcceptedJob(null)}
          onSuccess={handleInterestSuccess}
        />
      )}
      {removeInterestJobId && (
        <JobPostRemoveInterestDialog
          onClose={() => {
            setRemoveInterestJobId(null);
            setRemoveInterestPreviousDecision(null);
          }}
          onSuccess={handleInterestSuccess}
          jobId={removeInterestJobId}
          previousDecision={removeInterestPreviousDecision ?? undefined}
        />
      )}
      {checklistJobId && (
        <HiringProcessChecklistDialog
          jobPostId={checklistJobId}
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              setChecklistJobId(null);
            }
          }}
        />
      )}
    </div>
  );
}
